<script setup lang="ts">
type ButtonVariant = 'base' | 'chip' | 'cta' | 'danger' | 'ghost' | 'icon' | 'link' | 'primary' | 'secondary' | 'tab' | 'underline' | 'warning'
type ButtonSize = 'lg' | 'md' | 'sm'

const props = withDefaults(
  defineProps<{
    variant?: ButtonVariant
    size?: ButtonSize
    active?: boolean
    disabled?: boolean
    loading?: boolean
    type?: 'button' | 'reset' | 'submit'
    to?: string
    prefetch?: boolean
    /** Icon variant only: a destructive action, in danger. */
    danger?: boolean
  }>(),
  {
    variant: 'primary',
    size: 'md',
    active: false,
    disabled: false,
    loading: false,
    type: 'button',
    prefetch: true,
  },
)

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const baseClasses = 'inline-flex items-center justify-center gap-1.5 font-semibold transition-colors duration-200 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-accent-2 motion-reduce:transition-none'

// Actions are pills the height of the landing CTA; chips and tabs are smaller pills, and an
// underline tab is text over a rule, for the tabs under a dialog header.
const actionSizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-full',
  md: 'px-5 py-3 text-sm rounded-full',
  lg: 'px-6 py-3.5 text-base rounded-full',
}

const sizeClasses: Record<ButtonVariant, Record<ButtonSize, string>> = {
  primary: actionSizes,
  secondary: actionSizes,
  warning: actionSizes,
  danger: actionSizes,
  cta: actionSizes,
  base: {
    sm: 'h-9 px-3 text-sm rounded-xl',
    md: 'h-10 px-4 text-base rounded-xl',
    lg: 'h-11 px-5 text-base rounded-xl',
  },
  ghost: {
    sm: 'px-2 py-1 text-xs rounded-lg',
    md: 'px-3 py-1.5 text-sm rounded-lg',
    lg: 'px-4 py-2 text-base rounded-lg',
  },
  icon: {
    sm: 'h-7 w-7 rounded-lg',
    md: 'h-8 w-8 rounded-lg',
    lg: 'h-10 w-10 rounded-xl',
  },
  chip: {
    sm: 'px-2.5 py-1 text-xs rounded-full',
    md: 'px-3 py-1.5 text-sm rounded-full',
    lg: 'px-4 py-2 text-sm rounded-full',
  },
  tab: {
    sm: 'px-2.5 py-1 text-xs rounded-full',
    md: 'px-3 py-1 text-xs rounded-full',
    lg: 'px-4 py-1.5 text-sm rounded-full',
  },
  link: {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  },
  underline: {
    sm: 'px-3 py-2.5 text-xs',
    md: 'px-4 py-2.5 text-xs sm:py-3 sm:text-sm',
    lg: 'px-5 py-3 text-sm',
  },
}

// A tone pill is a soft wash with a rule of its tone (the landing's trial link); the CTA is the
// one solid fill. Secondary is the quiet hairline pill beside them. Base is the sign-in button in
// the Base network's own blue.
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border border-accent bg-accent-soft text-ink hover:bg-accent/22',
  secondary: 'border border-rule bg-panel text-ink hover:border-ink-faint hover:bg-ink/6',
  warning: 'border border-warn bg-warn-soft text-ink hover:bg-warn/22',
  danger: 'border border-danger bg-danger-soft text-ink hover:bg-danger/22',
  cta: 'bg-accent text-paper-2 shadow-lg shadow-accent/30 hover:-translate-y-0.5 hover:shadow-accent/45 motion-reduce:hover:translate-y-0',
  base: 'bg-[#0052FF] text-white hover:bg-[#0047E0] dark:bg-white dark:text-[#0052FF]',
  ghost: 'text-ink-soft hover:bg-ink/6 hover:text-ink',
  icon: 'text-ink-faint hover:bg-accent-2-soft hover:text-ink',
  chip: 'border border-rule bg-panel text-ink-soft hover:bg-ink/6 hover:text-ink',
  tab: 'text-ink-soft hover:bg-ink/6 hover:text-ink',
  link: 'text-ink-soft underline decoration-rule underline-offset-2 hover:text-ink',
  underline: '-mb-px border-b-2 border-transparent text-ink-faint hover:text-ink',
}

const activeClasses: Record<ButtonVariant, string> = {
  primary: '',
  secondary: '',
  ghost: '',
  icon: '',
  cta: '',
  base: '',
  chip: 'border border-accent-2 bg-accent-2-soft text-ink',
  link: '',
  tab: 'bg-panel text-ink shadow-sm ring-1 ring-rule-soft',
  underline: '-mb-px border-b-2 border-accent text-accent',
  warning: '',
  danger: '',
}

const disabledClasses = 'opacity-50 cursor-not-allowed pointer-events-none'

const buttonClasses = computed(() => {
  const classes = [
    'ui-button',
    baseClasses,
    sizeClasses[props.variant][props.size],
  ]

  if (props.active && activeClasses[props.variant]) {
    classes.push(activeClasses[props.variant])
  }
  else if (props.danger && props.variant === 'icon') {
    classes.push('text-danger hover:bg-danger-soft')
  }
  else {
    classes.push(variantClasses[props.variant])
  }

  if (props.disabled || props.loading) {
    classes.push(disabledClasses)
  }

  return classes
})

const isExternalUrl = computed(() => props.to?.startsWith('http'))

const componentIs = computed(() => {
  if (!props.to)
    return 'button'
  if (isExternalUrl.value)
    return 'a'
  return resolveComponent('NuxtLink')
})

const componentProps = computed(() => {
  if (!props.to) {
    return {
      type: props.type,
      disabled: props.disabled || props.loading,
    }
  }

  return isExternalUrl.value
    ? { href: props.to }
    : { to: props.to, prefetch: props.prefetch }
})

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<template>
  <component
    :is="componentIs"
    v-bind="componentProps"
    :class="buttonClasses"
    @click="handleClick"
  >
    <span v-if="props.loading" class="mr-2 icon-[svg-spinners--ring-resize] size-4" />
    <slot />
  </component>
</template>
